import React, { FC } from 'react';
import clsx from 'clsx';

import styles from './Typography.module.scss';
import { COLORS } from './Typography.const';
import colors from 'styles/_colors.module.scss';

type TypographyElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'span' | 'p' | 'a';

type TypographyProps = {
  bold?: boolean;
  disabled?: boolean;
  align?: 'left' | 'center' | 'right';
  dangerouslyUsedClassName?: string;
  color?: typeof COLORS[keyof typeof COLORS];
};

type BaseProps = {
  element: TypographyElement;
} & TypographyProps &
  React.ComponentProps<'span'>;

const Base: FC<BaseProps> = ({
  bold = false,
  align,
  color = COLORS.TEXT_PRIMARY,
  disabled = false,
  className,
  dangerouslyUsedClassName,
  children,
  element,
  ...props
}) => {
  return React.createElement(
    element,
    {
      className: clsx(className, {
        [styles.bold]: bold,
        [styles.disabled]: disabled,
        [`${dangerouslyUsedClassName}`]: dangerouslyUsedClassName,
      }),
      style: { color: colors[color], textAlign: align },
      ...props,
    },
    children
  );
};

type TitleProps = BaseProps & TypographyProps & React.ComponentProps<'h1'>;

const Title: FC<TitleProps> = ({ children, element, ...props }) => {
  return (
    <Base element={element} className={styles.title} {...props}>
      {children}
    </Base>
  );
};

type Heading1Props = TypographyProps & React.ComponentProps<'h2'>;

const Heading1: FC<Heading1Props> = ({ children, ...props }) => {
  return (
    <Base element="h2" className={styles.heading1} {...props}>
      {children}
    </Base>
  );
};

type Heading2Props = TypographyProps & React.ComponentProps<'h3'>;

const Heading2: FC<Heading2Props> = ({ children, ...props }) => {
  return (
    <Base element="h3" className={styles.heading2} {...props}>
      {children}
    </Base>
  );
};

type BodyVariant = 'primary' | 'secondary' | 'tertiary';
type BodyProps = {
  variant?: BodyVariant;
} & TypographyProps & React.ComponentProps<'span'>;
const BODY_STYLE_MAP: Record<BodyVariant, string> = {
  primary: styles.textPrimary,
  secondary: styles.textSecondary,
  tertiary: styles.textTertiary
};

const Body: FC<BodyProps> = ({ children, variant = 'primary', ...props }) => {
  const styleName = BODY_STYLE_MAP[variant];

  return (
    <Base element="span" className={styleName} {...props}>
      {children}
    </Base>
  );
};

type SubHeadingProps = TypographyProps & React.ComponentProps<'h4'>;
const SubHeading: FC<SubHeadingProps> = ({ children, ...props }) => {
  return (
    <Base element="h4" className={styles.subHeading} {...props}>
      {children}
    </Base>
  );
};

export const Typography = {
  Title,
  Body,
  Heading1,
  Heading2,
  SubHeading,
};

// eslint-disable-next-line import/no-default-export
export default Typography;
